// src/components/album/voice/GalleryVoiceBanner.jsx
import React, { useState, useRef } from 'react';
import { Mic, Play, Pause, Volume2, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';

export const GalleryVoiceBanner = ({ gallery, voiceSettings, onSegmentPlay }) => {
  const { galleryVoice, voiceSegments } = gallery;
  const [isPlaying, setIsPlaying] = useState(false);
  const [showSegments, setShowSegments] = useState(false);
  const audioRef = useRef(null);

  if (!galleryVoice && (!voiceSegments || voiceSegments.length === 0)) {
    return null;
  }

  const displayMode = voiceSettings?.galleryNarration?.displayMode || 'banner';
  
  if (displayMode !== 'banner') return null;

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div className="mb-8 space-y-4">
      {/* Main Gallery Narration */}
      {galleryVoice && (
        <div className="bg-gradient-to-r from-purple-50 via-pink-50 to-blue-50 rounded-xl p-6 border-2 border-purple-200 shadow-md">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center flex-shrink-0 shadow-lg">
              <Mic className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-bold text-lg text-gray-900">Gallery Story</h3>
                <span className="px-2 py-0.5 bg-purple-600 text-white text-xs rounded-full">
                  {Math.floor(galleryVoice.duration / 60)}:{String(galleryVoice.duration % 60).padStart(2, '0')}
                </span>
              </div>
              
              {galleryVoice.speaker && (
                <p className="text-sm text-gray-600 mb-3">
                  Narrated by <span className="font-medium">{galleryVoice.speaker.name}</span>
                  {galleryVoice.speaker.role && ` • ${galleryVoice.speaker.role}`}
                </p>
              )}
              
              {galleryVoice.transcript && (
                <p className="text-sm text-gray-700 mb-4 line-clamp-2">
                  {galleryVoice.transcript}
                </p>
              )}
              
              <button
                onClick={togglePlay}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-md hover:shadow-lg transform hover:scale-105"
              >
                {isPlaying ? (
                  <>
                    <Pause className="w-4 h-4" />
                    Pause
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Listen to Gallery Story
                  </>
                )}
              </button>
            </div>
          </div>
          
          <audio 
            ref={audioRef} 
            src={galleryVoice.url}
            onEnded={() => setIsPlaying(false)}
          />
        </div>
      )}

      {/* Voice Segments */}
      {voiceSegments && voiceSegments.length > 0 && voiceSettings?.galleryNarration?.enableSegments && (
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
          <button
            onClick={() => setShowSegments(!showSegments)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <BookOpen className="w-5 h-5 text-purple-600" />
              <h4 className="font-semibold text-gray-900">
                Story Chapters ({voiceSegments.length})
              </h4>
            </div>
            {showSegments ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {showSegments && (
            <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {voiceSegments.map((segment, index) => (
                <button
                  key={segment.id}
                  onClick={() => onSegmentPlay?.(segment)}
                  className="flex items-center gap-3 p-3 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg hover:shadow-md transition-all border border-purple-100 hover:border-purple-300 group"
                >
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center flex-shrink-0 group-hover:from-purple-200 group-hover:to-pink-200 transition-colors">
                    <span className="font-bold text-purple-600">{index + 1}</span>
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <h5 className="font-medium text-sm text-gray-900 truncate">
                      {segment.title}
                    </h5>
                    <p className="text-xs text-gray-500">
                      {segment.speaker?.name} • {segment.duration}s
                    </p>
                  </div>
                  <Play className="w-4 h-4 text-purple-600 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};